// JamTakeService — local persistence for Jam Takes (localStorage in Build 1).
// The interface is stable so a remote backend can replace it later.

import { JamTake, GuidanceMode, SessionSummary } from "@/models/types";

const KEY = "adaptivejam.takes";

function readAll(): JamTake[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as JamTake[]) : [];
  } catch {
    return [];
  }
}

function writeAll(takes: JamTake[]): void {
  localStorage.setItem(KEY, JSON.stringify(takes));
}

function uid(): string {
  return `take_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export const JamTakeService = {
  list(): JamTake[] {
    return readAll().sort((a, b) => b.dateISO.localeCompare(a.dateISO));
  },

  get(id: string): JamTake | undefined {
    return readAll().find((t) => t.id === id);
  },

  save(input: {
    songId: string;
    songTitle: string;
    versionName: string;
    mode: GuidanceMode;
    lastTime: number;
    variation?: number;
    reflection?: string;
    summary?: SessionSummary;
    progress?: JamTake["progress"];
  }): JamTake {
    const takes = readAll();
    const take: JamTake = {
      id: uid(),
      songId: input.songId,
      songTitle: input.songTitle,
      versionName: input.versionName,
      dateISO: new Date().toISOString(),
      mode: input.mode,
      progress: input.progress ?? "Saved",
      variation: input.variation ?? 0,
      lastTime: input.lastTime,
      reflection: input.reflection,
      summary: input.summary,
    };
    takes.push(take);
    writeAll(takes);
    return take;
  },

  rename(id: string, versionName: string): void {
    const takes = readAll();
    const t = takes.find((x) => x.id === id);
    if (t) {
      t.versionName = versionName;
      writeAll(takes);
    }
  },

  duplicate(id: string): JamTake | undefined {
    const takes = readAll();
    const src = takes.find((x) => x.id === id);
    if (!src) return undefined;
    const copy: JamTake = {
      ...src,
      id: uid(),
      versionName: `${src.versionName} (copy)`,
      dateISO: new Date().toISOString(),
      variation: src.variation + 1,
    };
    takes.push(copy);
    writeAll(takes);
    return copy;
  },

  remove(id: string): void {
    writeAll(readAll().filter((t) => t.id !== id));
  },
};
